import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Layers, Send, Timer } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/v2/components/ui/popover";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/v2/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/v2/components/ui/tooltip";
import { format, getISOWeek } from "date-fns";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/v2/components/ui/button";
import { Card } from "@/v2/components/ui/card";
import { InfiniteCalendar } from "@/v2/components/InfiniteCalendar";
import { MonthPicker } from "@/shared/components/ui/monthpicker";
import { cn } from "@/v2/lib/utils";
import { useDateParams } from "@/v2/hooks/useDateParams";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useIsMobile } from "@/v2/hooks/useIsMobile";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export type DaySums = { exported: number; tracked: number };
export type CalendarViewMode = "all" | "tracked" | "exported";

export interface CalendarGridProps {
	dailySums?: Map<string, DaySums>;
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const WEEKDAYS_LETTER = ["S", "M", "T", "W", "T", "F", "S"];
const WEEKDAYS_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function isSameDay(a: Date, b: Date) {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isSameMonth(a: Date, b: Date) {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function formatDateParam(d: Date) {
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dayKey(d: Date) {
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isInRange(d: Date, from: Date | undefined, to: Date | undefined) {
	if (!from || !to) return false;
	return d.getTime() >= from.getTime() && d.getTime() <= to.getTime();
}

function buildCalendarGrid(year: number, month: number) {
	const firstOfMonth = new Date(year, month, 1);
	const startOffset = firstOfMonth.getDay();
	const daysInMonth = new Date(year, month + 1, 0).getDate();
	const weeksNeeded = Math.ceil((startOffset + daysInMonth) / 7);
	const totalCells = weeksNeeded * 7;

	const startDay = new Date(firstOfMonth);
	startDay.setDate(startDay.getDate() - startOffset);

	const days: Date[] = [];
	for (let i = 0; i < totalCells; i++) {
		const d = new Date(startDay);
		d.setDate(startDay.getDate() + i);
		days.push(d);
	}
	return { days, weeksNeeded };
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

export default function Calendar({ dailySums = new Map() }: CalendarGridProps) {
	useDocumentTitle("Calendar");
	const { date: selectedDate, dateRange } = useDateParams();
	const hasRange = dateRange.from != null && dateRange.to != null;
	const navigate = useNavigate();
	const today = useMemo(() => new Date(), []);
	const [searchParams, setSearchParams] = useSearchParams();
	const viewMode = (searchParams.get("view") || "exported") as CalendarViewMode;
	const setViewMode = useCallback((mode: CalendarViewMode) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (mode === "exported") next.delete("view");
			else next.set("view", mode);
			return next;
		}, { replace: true });
	}, [setSearchParams]);

	const [viewedMonth, setViewedMonth] = useState(() => ({
		year: selectedDate.getFullYear(),
		month: selectedDate.getMonth(),
	}));

	// Sync viewed month when selected date or date range changes externally
	useEffect(() => {
		const anchor = hasRange && dateRange.from ? dateRange.from : selectedDate;
		setViewedMonth({ year: anchor.getFullYear(), month: anchor.getMonth() });
	}, [selectedDate, dateRange.from, dateRange.to, hasRange]);

	const { days: calendarDays, weeksNeeded } = useMemo(
		() => buildCalendarGrid(viewedMonth.year, viewedMonth.month),
		[viewedMonth.year, viewedMonth.month],
	);

	// Build weekly sums per row
	const weeklySums = useMemo(() => {
		const sums: DaySums[] = [];
		for (let week = 0; week < weeksNeeded; week++) {
			let exported = 0;
			let tracked = 0;
			for (let d = 0; d < 7; d++) {
				const day = calendarDays[week * 7 + d];
				const ds = dailySums.get(dayKey(day));
				if (ds) {
					exported += ds.exported;
					tracked += ds.tracked;
				}
			}
			sums.push({ exported: Math.round(exported * 10) / 10, tracked: Math.round(tracked * 10) / 10 });
		}
		return sums;
	}, [calendarDays, weeksNeeded, dailySums]);

	// Monthly sums for the viewed year (all 12 months)
	const monthlySums = useMemo(() => {
		const sums: DaySums[] = Array.from({ length: 12 }, () => ({ exported: 0, tracked: 0 }));
		dailySums.forEach((ds, key) => {
			const [yearStr, monthStr] = key.split("-");
			const year = parseInt(yearStr, 10);
			const month = parseInt(monthStr, 10) - 1;
			if (year === viewedMonth.year && month >= 0 && month < 12) {
				sums[month].exported += ds.exported;
				sums[month].tracked += ds.tracked;
			}
		});
		return sums.map((s) => ({
			exported: Math.round(s.exported * 10) / 10,
			tracked: Math.round(s.tracked * 10) / 10,
		}));
	}, [dailySums, viewedMonth.year]);

	// Yearly total
	const yearlyTotal = useMemo(() => {
		const total: DaySums = { exported: 0, tracked: 0 };
		for (const ms of monthlySums) {
			total.exported += ms.exported;
			total.tracked += ms.tracked;
		}
		return {
			exported: Math.round(total.exported * 10) / 10,
			tracked: Math.round(total.tracked * 10) / 10,
		};
	}, [monthlySums]);

	// Auto-scroll the monthly strip to center the current month
	const monthStripRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!monthStripRef.current) return;
		const activeEl = monthStripRef.current.querySelector("[data-active=true]");
		if (activeEl) {
			activeEl.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
		}
	}, [viewedMonth.month, viewedMonth.year]);

	const goToPreviousMonth = useCallback(() => {
		setViewedMonth((prev) => {
			const d = new Date(prev.year, prev.month - 1, 1);
			return { year: d.getFullYear(), month: d.getMonth() };
		});
	}, []);

	const goToNextMonth = useCallback(() => {
		setViewedMonth((prev) => {
			const d = new Date(prev.year, prev.month + 1, 1);
			return { year: d.getFullYear(), month: d.getMonth() };
		});
	}, []);

	const goToToday = useCallback(() => {
		setViewedMonth({ year: today.getFullYear(), month: today.getMonth() });
	}, [today]);

	const onSelectDate = (d: Date) => {
		if (isSameDay(d, today)) {
			navigate("/tracked");
		} else {
			navigate(`/tracked?date=${formatDateParam(d)}`);
		}
	};

	const viewedMonthDate = new Date(viewedMonth.year, viewedMonth.month, 1);
	const isMobile = useIsMobile();

	// Mobile: infinite scroll calendar
	if (isMobile) {
		return (
			<InfiniteCalendar
				dailySums={dailySums}
				viewMode={viewMode}
				onViewModeChange={setViewMode}
				selectedDate={selectedDate}
				dateRange={dateRange}
			/>
		);
	}

	// Desktop: grid calendar
	return (
		<div className="flex h-full flex-col p-3 sm:p-6">
			{/* Action bar */}
			<div className="flex items-center gap-2 mb-3">
				<h1 className="text-4xl font-semibold tracking-tight text-muted-foreground">Calendar</h1>
				<div className="ml-auto">
					<Tabs value={viewMode} onValueChange={(v) => setViewMode(v as CalendarViewMode)}>
						<TabsList className="h-7 sm:h-8">
							<TabsTrigger value="exported" className="text-[11px] sm:text-xs px-2 sm:px-3 h-5 sm:h-6 gap-1">
								<Send className="size-3" />Exported
							</TabsTrigger>
							<TabsTrigger value="tracked" className="text-[11px] sm:text-xs px-2 sm:px-3 h-5 sm:h-6 gap-1">
								<Timer className="size-3" />Tracked
							</TabsTrigger>
							<TabsTrigger value="all" className="text-[11px] sm:text-xs px-2 sm:px-3 h-5 sm:h-6 gap-1">
								<Layers className="size-3" />All
							</TabsTrigger>
						</TabsList>
					</Tabs>
				</div>
			</div>

			{/* Monthly totals strip + yearly total */}
			<Card className="flex mb-3 overflow-hidden">
				<div className="flex-1 flex overflow-x-auto scrollbar-hide" ref={monthStripRef}>
					{monthlySums.map((ms, i) => {
						const isActive = i === viewedMonth.month;
						const hasData = ms.exported > 0 || ms.tracked > 0;
						return (
							<button
								key={i}
								data-active={isActive}
								onClick={() => setViewedMonth((prev) => ({ ...prev, month: i }))}
								className={cn(
									"flex-1 min-w-[60px] sm:min-w-[80px] flex flex-col items-start px-2 py-2 sm:px-3 sm:py-2.5 transition-colors hover:bg-muted/50 rounded-md",
								)}
							>
								<span className={cn(
									"text-[10px] sm:text-xs font-medium",
									isActive ? "text-primary" : "text-muted-foreground",
								)}>
									<span className="sm:hidden">{format(new Date(viewedMonth.year, i, 1), "MMM")}</span>
									<span className="hidden sm:inline">{format(new Date(viewedMonth.year, i, 1), "MMMM")}</span>
								</span>
								{hasData ? (
									<div className="flex flex-col items-start mt-0.5">
										{viewMode !== "tracked" && (
											<span className="text-[10px] sm:text-xs font-semibold tabular-nums min-w-[2.5em]">
												{ms.exported.toFixed(1)}h
											</span>
										)}
										{viewMode !== "exported" && (
											<span className="text-[10px] sm:text-xs text-muted-foreground tabular-nums min-w-[2.5em]">
												{ms.tracked.toFixed(1)}h
											</span>
										)}
									</div>
								) : (
									<div className="flex flex-col items-start mt-0.5">
										{viewMode !== "tracked" && (
											<span className="text-[10px] sm:text-xs text-muted-foreground/40 tabular-nums min-w-[2.5em]">0.0h</span>
										)}
										{viewMode !== "exported" && (
											<span className="text-[10px] sm:text-xs text-muted-foreground/40 tabular-nums min-w-[2.5em]">0.0h</span>
										)}
									</div>
								)}
							</button>
						);
					})}
				</div>

				{/* Yearly total */}
				<div className="shrink-0 flex flex-col items-start px-3 py-2 sm:px-5 sm:py-2.5 border-l-2 border-border ml-2 min-w-[6rem] sm:min-w-[9rem]">
					<span className="text-[10px] sm:text-xs font-medium text-muted-foreground">
						{viewedMonth.year === today.getFullYear() ? `${viewedMonth.year} Year to Date` : `${viewedMonth.year}`}
					</span>
					<div className="flex flex-col items-start mt-0.5">
						{viewMode !== "tracked" && (
							<span className="text-[10px] sm:text-xs font-semibold tabular-nums min-w-[2.5em]">
								{yearlyTotal.exported.toFixed(1)}h
							</span>
						)}
						{viewMode !== "exported" && (
							<span className="text-[10px] sm:text-xs text-muted-foreground tabular-nums min-w-[2.5em]">
								{yearlyTotal.tracked.toFixed(1)}h
							</span>
						)}
					</div>
				</div>
			</Card>

			<Card className="flex flex-1 flex-col overflow-hidden">
				{/* Header */}
				<div className="flex items-center gap-1 px-3 py-2 sm:gap-2 sm:px-4 sm:py-3 border-b border-border bg-neutral-100 dark:bg-transparent">
					<div className="flex items-center">
						<Button variant="ghost" size="icon" onClick={goToPreviousMonth} className="h-7 w-7 text-muted-foreground sm:h-8 sm:w-8">
							<ChevronLeft className="size-3.5 sm:size-4" />
						</Button>
						<Button variant="ghost" size="icon" onClick={goToNextMonth} className="h-7 w-7 text-muted-foreground sm:h-8 sm:w-8">
							<ChevronRight className="size-3.5 sm:size-4" />
						</Button>
					</div>

					<Popover>
						<PopoverTrigger asChild>
							<button className="inline-flex items-center gap-1 text-sm font-medium sm:text-xl sm:font-semibold hover:text-foreground/80 transition-colors">
								{format(viewedMonthDate, "MMMM yyyy")}
								<ChevronDown className="size-3 sm:size-4 text-muted-foreground" />
							</button>
						</PopoverTrigger>
						<PopoverContent align="start" className="w-auto p-0">
							<MonthPicker
								selectedMonth={viewedMonthDate}
								onMonthSelect={(d) => setViewedMonth({ year: d.getFullYear(), month: d.getMonth() })}
							/>
						</PopoverContent>
					</Popover>

					<Button
						variant="ghost"
						size="sm"
						onClick={goToToday}
						className="ml-auto h-7 gap-1 text-[11px] sm:h-8 sm:gap-1.5 sm:text-sm"
					>
						<CalendarDays className="size-3 sm:size-4" />
						Today
					</Button>
				</div>

				{/* Grid */}
				<div
					className="grid flex-1"
					style={{
						gridTemplateColumns: "repeat(7, 1fr) auto",
						gridTemplateRows: `auto repeat(${weeksNeeded}, 1fr)`,
					}}
				>
					{/* Weekday headers */}
					{WEEKDAYS_FULL.map((day, i) => (
						<div
							key={day}
							className={cn(
								"border-b border-border py-2 text-xs text-muted-foreground sm:py-2.5 sm:text-[13px] bg-neutral-100 dark:bg-transparent",
								"text-center sm:text-left sm:pl-3",
								i > 0 && "border-l border-l-border",
							)}
						>
							<span className="sm:hidden">{WEEKDAYS_LETTER[i]}</span>
							<span className="hidden sm:inline">{day}</span>
						</div>
					))}
					{/* Week column header */}
					<div className="border-b border-border border-l-2 border-l-border py-2 text-center text-xs text-muted-foreground sm:py-2.5 sm:text-[13px] bg-neutral-100 dark:bg-transparent sm:px-3">
						<span className="sm:hidden">Wk</span>
						<span className="hidden sm:inline">Week</span>
					</div>

					{/* Rows */}
					{Array.from({ length: weeksNeeded }).map((_, weekIdx) => {
						const weekDays = calendarDays.slice(weekIdx * 7, weekIdx * 7 + 7);
						const weekNum = getISOWeek(weekDays[3]);
						const ws = weeklySums[weekIdx];

						return (
							<React.Fragment key={weekIdx}>
								{weekDays.map((currDay, col) => {
									const isCurrMonth = isSameMonth(currDay, viewedMonthDate);
									const isDayToday = isSameDay(currDay, today);
									const isSelected = !hasRange && isSameDay(currDay, selectedDate);
									const inRange = hasRange && isInRange(currDay, dateRange.from, dateRange.to);
									const rangeStart = hasRange && dateRange.from && isSameDay(currDay, dateRange.from);
									const rangeEnd = hasRange && dateRange.to && isSameDay(currDay, dateRange.to);
									const isFuture = currDay.getTime() > today.getTime() && !isDayToday;
									// Show month prefix at boundaries: when adjacent day is a different month
								const globalIdx = weekIdx * 7 + col;
								const prevDay = globalIdx > 0 ? calendarDays[globalIdx - 1] : null;
								const nextDay = globalIdx < calendarDays.length - 1 ? calendarDays[globalIdx + 1] : null;
								const showMonthPrefix =
									(prevDay && prevDay.getMonth() !== currDay.getMonth()) ||
									(nextDay && nextDay.getMonth() !== currDay.getMonth());
									const hasCircle = isDayToday || isSelected;

									const ds = dailySums.get(dayKey(currDay));
									const hasAnyData = ds && (ds.exported > 0 || ds.tracked > 0);
									const showExported = hasAnyData && viewMode !== "tracked";
									const showTracked = hasAnyData && viewMode !== "exported";
									const hasHours = showExported || showTracked;

									return (
										<button
											key={`${weekIdx}-${col}`}
											onClick={() => onSelectDate(currDay)}
											className={cn(
												"flex flex-col border-t border-border p-1 text-left transition-colors bg-background hover:bg-muted/50 sm:p-2",
												col > 0 && "border-l border-l-border",
												!isCurrMonth && "text-muted-foreground/40",
												isFuture && isCurrMonth && "text-muted-foreground/60",
												isDayToday && "ring-[1.5px] ring-inset ring-highlight",
												isSelected && !isDayToday && "ring-[1.5px] ring-inset ring-primary",
												inRange && "bg-primary/5",
												rangeStart && "bg-primary/8",
												rangeEnd && "bg-primary/8",
											)}
										>
											<div className="flex items-center gap-1">
												{showMonthPrefix && (
													<span className="text-[10px] sm:text-xs leading-none text-muted-foreground">
														{format(currDay, "MMM")}
													</span>
												)}
												{hasCircle ? (
													<span
														className={cn(
															"flex h-5 w-5 items-center justify-center rounded-full text-xs sm:h-6 sm:w-6 sm:text-sm",
															isDayToday && "bg-highlight text-white",
															isSelected && !isDayToday && "bg-primary text-primary-foreground",
														)}
													>
														{currDay.getDate()}
													</span>
												) : (
													<span className="text-xs sm:text-sm leading-none text-muted-foreground">
														{currDay.getDate()}
													</span>
												)}
											</div>
											{hasHours && (
												<TooltipProvider delayDuration={200}>
													<Tooltip>
														<TooltipTrigger asChild>
															<div className="mt-auto flex flex-col items-end gap-0 sm:gap-0.5 w-fit p-0.5 rounded">
																{showExported && (
																	<span className="text-[10px] sm:text-xs leading-tight font-medium tabular-nums min-w-[2.5em]">
																		{ds!.exported.toFixed(1)}h
																	</span>
																)}
																{showTracked && (
																	<span className="text-[10px] sm:text-xs leading-tight text-muted-foreground tabular-nums min-w-[2.5em]">
																		{ds!.tracked.toFixed(1)}h
																	</span>
																)}
															</div>
														</TooltipTrigger>
														<TooltipContent side="top" align="center" className="px-3 py-2">
															<div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs tabular-nums">
																<span className="text-right">{(ds?.exported ?? 0).toFixed(1)}h</span>
																<span className="text-muted-foreground">exported</span>
																<span className="text-right">{(ds?.tracked ?? 0).toFixed(1)}h</span>
																<span className="text-muted-foreground">tracked</span>
															</div>
														</TooltipContent>
													</Tooltip>
												</TooltipProvider>
											)}
										</button>
									);
								})}

								{/* Week cell */}
								<button
									key={`week-${weekIdx}`}
									onClick={() => {
										const from = weekDays[0];
										const to = weekDays[6];
										navigate(`/tracked?from=${formatDateParam(from)}&to=${formatDateParam(to)}`);
									}}
									className="flex flex-col items-center justify-between border-t border-border border-l-2 border-l-border bg-muted/20 p-1 sm:p-2 hover:bg-muted/40 transition-colors cursor-pointer"
								>
									<span className="text-[9px] sm:text-[10px] font-medium text-muted-foreground/60">
										W{weekNum}
									</span>
									{(ws.exported > 0 || ws.tracked > 0) && (
										<TooltipProvider delayDuration={200}>
											<Tooltip>
												<TooltipTrigger asChild>
													<div className="flex flex-col items-end gap-0 sm:gap-0.5 mt-auto px-1 p-0.5 rounded w-fit">
														{viewMode !== "tracked" && (
															<span className="text-[10px] sm:text-xs leading-tight font-semibold tabular-nums min-w-[2.5em]">
																{ws.exported.toFixed(1)}h
															</span>
														)}
														{viewMode !== "exported" && (
															<span className="text-[10px] sm:text-xs leading-tight text-muted-foreground font-medium tabular-nums min-w-[2.5em]">
																{ws.tracked.toFixed(1)}h
															</span>
														)}
													</div>
												</TooltipTrigger>
												<TooltipContent side="left" align="center" className="px-3 py-2">
													<div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs tabular-nums">
														<span className="text-right">{ws.exported.toFixed(1)}h</span>
														<span className="text-muted-foreground">exported</span>
														<span className="text-right">{ws.tracked.toFixed(1)}h</span>
														<span className="text-muted-foreground">tracked</span>
													</div>
												</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									)}
								</button>
							</React.Fragment>
						);
					})}
				</div>
			</Card>
		</div>
	);
}
